<template>
    <div class="warehouse-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
            @click:row="onRowClick"
        >
            <template #loading>
                <div class="warehouse-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.asseco_id="elem">
                <div v-if="elem.item.asseco_id != null">{{ elem.item.asseco_id }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.name="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.address="elem">
                <div v-if="elem.item.address != null">{{ elem.item.address }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.admins_count="elem">
                <div v-if="elem.item.admins_count != null">{{ elem.item.admins_count }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.ph_count="elem">
                <div v-if="elem.item.users_count != null">{{ elem.item.users_count }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.active_count="elem">
                <div v-if="elem.item.actives_count != null">{{ elem.item.actives_count }}</div>
                <div v-else class="warehouse-table__none">{{ $t('warehouse.none') }}</div>
            </template>

            <template #item.amount="elem">
                <div v-if="elem.item.amount != null">{{ elem.item.amount }}</div>
                <div v-else class="warehouse-table__none">{{ 0.00 }}</div>
            </template>

            <template #item.actions="elem">
                <div v-if="!isAdminWholesaler" class="warehouse-table__action-wrapper" @click.stop>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover warehouse-table__icon-wrapper warehouse-table__icon-wrapper--edit" @click="navigateTo(`/warehouse/${elem.item.id}`)">
                                <KIcon icon="edit" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('warehouse.edit') }}</div>
                        </template>
                    </KTooltip>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover warehouse-table__icon-wrapper warehouse-table__icon-wrapper--trash" @click="openDeleteWarehouseDialog(elem.item.id)">
                                <KIcon icon="trash" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('warehouse.delete') }}</div>
                        </template>
                    </KTooltip>
                </div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>

        <KDialog
            :is-open-dialog-action="isOpenDeleteDialog"
            :is-close-dialog-action="isCloseDeleteDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="warehouse-table__dialog-title">{{ $t('warehouse.areYouSureDeleteWarehouse') }}</div>
                    <div class="warehouse-table__btn-dialog-wrapper">
                        <KButton
                            id="warehouse-cancel"
                            :label="$t('warehouse.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="warehouse-delete"
                            :label="$t('warehouse.deleteBtn')"
                            type="submit"
                            secondary
                            :loading="loadingDeleteBtn"
                            @click="deleteAction"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import WarehouseApi from '@/api/v1/WarehouseApi';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { toast } from 'vue3-toastify';
import { navigateTo } from '@/hooks/NavigateHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IWareHouseItems } from '@/models/WarehouseModel';
import KDialog from '@/components/atoms/KDialog.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KTooltip from '@/components/atoms/KTooltip.vue';
import KButton from '@/components/atoms/KButton.vue';
import KTextField from '@/components/atoms/KTextField.vue';

const { grey400 } = variables;
const { isAdminWholesaler } = storeToRefs(useUserStore());

const headers = ref<ITableHeader[]>([
    {
        title: t('warehouse.idAsseco'),
        align: 'start',
        sortable: false,
        key: 'asseco_id',
        width: '',
    },
    {
        title: t('warehouse.name'),
        align: 'center',
        sortable: false,
        key: 'name',
        width: '',
    },
    {
        title: t('warehouse.address'),
        align: 'center',
        sortable: false,
        key: 'address',
        width: '',
    },
    {
        title: t('warehouse.admins'),
        align: 'center',
        sortable: false,
        key: 'admins_count',
        width: '',
    },
    {
        title: t('warehouse.ph'),
        align: 'center',
        sortable: false,
        key: 'ph_count',
        width: '',
    },
    {
        title: t('warehouse.active'),
        align: 'center',
        sortable: false,
        key: 'active_count',
        width: '',
    },
    {
        title: t('warehouse.amount'),
        align: 'center',
        sortable: false,
        key: 'amount',
        width: '',
    },
    {
        title: '',
        align: 'center',
        sortable: false,
        key: 'actions',
        width: '',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IWareHouseItems[]>([]);
const warehouseApi = new WarehouseApi();
const warehouseList = async() => {
    loading.value = true;
    try {
        const result = await warehouseApi.warehouseList(
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('warehouse.toast.errorWarehouseList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('warehouse.toast.errorWarehouseList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    warehouseList();
};

const isOpenDeleteDialog = ref<boolean>(false);
const isCloseDeleteDialog = ref<boolean>(true);
const selectedWarehouseId = ref<number | null>(null);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDeleteDialog.value = false;
    isCloseDeleteDialog.value = true;
    selectedWarehouseId.value = null;
};
const openDeleteWarehouseDialog = (id: number) => {
    selectedWarehouseId.value = id;
    isOpenDeleteDialog.value = true;
    isCloseDeleteDialog.value = false;
};
const loadingDeleteBtn = ref<boolean>(false);
const deleteAction = async() => {
    loadingDeleteBtn.value = true;
    try {
        const result = await warehouseApi.deleteWarehouse(selectedWarehouseId.value);
        if (!result.isSuccess) {
            toast.error(t('warehouse.toast.errorWarehouseDeleteAction'));
            loadingDeleteBtn.value = false;
            closeDialogWhenNoTargetPressed();
            return;
        }
        toast.success(t('warehouse.toast.successWarehouseDeleteAction'));
        closeDialogWhenNoTargetPressed();
        warehouseList();
    } catch (e) {
        toast.error(t('warehouse.toast.errorWarehouseDeleteAction'));
        loadingDeleteBtn.value = false;
        closeDialogWhenNoTargetPressed();
        return;
    }
    loadingDeleteBtn.value = false;
};

const onRowClick = (event: any, item: any) => {
    navigateTo(`/warehouse/details/${item.item.id}`);
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        warehouseList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.warehouse-table {
    &__loading {
        opacity: 0.6;
    }

    &__none {
        color: $red-400;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &:deep(.v-data-table tr:hover) {
        .warehouse-table__icon-wrapper--edit svg {
            fill: $primary-800;
        }

        .warehouse-table__icon-wrapper--trash svg {
            fill: $red-400;
        }
    }
}
</style>
